import { useState, useCallback, useEffect } from "react";
import { useCompilerErrors } from "./use-compiler-errors";
import { useAuthErrors } from "./use-auth-errors";
import { useLlmErrors } from "./use-llm-errors";
import { useFunctionErrors } from "./use-function-errors";
import { useExtractionErrors } from "./use-extraction-errors";
import type { ErrorSource, ErrorSeverity, ErrorFilters } from "@/lib/error-types";
import { subDays } from "date-fns";

// Date range options
export const DATE_RANGES = [
  { value: "24h", label: "Last 24 hours", days: 1 },
  { value: "7d", label: "Last 7 days", days: 7 },
  { value: "30d", label: "Last 30 days", days: 30 },
] as const;

export type DateRangeValue = typeof DATE_RANGES[number]["value"];

interface UseErrorConsoleOptions {
  initialTab?: ErrorSource;
  initialDateRange?: DateRangeValue;
  initialSeverity?: ErrorSeverity | 'all';
}

interface UseErrorConsoleResult {
  // Current tab state
  activeTab: ErrorSource;
  setActiveTab: (tab: ErrorSource) => void;

  // Date range state
  dateRange: DateRangeValue;
  setDateRange: (range: DateRangeValue) => void;

  // Severity filter state
  severityFilter: ErrorSeverity | 'all';
  setSeverityFilter: (severity: ErrorSeverity | 'all') => void;

  // Error code filter
  selectedErrorCode: string | null;
  setSelectedErrorCode: (code: string | null) => void;

  // Hook results for current tab
  compilerHook: ReturnType<typeof useCompilerErrors>;
  authHook: ReturnType<typeof useAuthErrors>;
  llmHook: ReturnType<typeof useLlmErrors>;
  functionHook: ReturnType<typeof useFunctionErrors>;
  extractionHook: ReturnType<typeof useExtractionErrors>;

  // Helper to get current hook based on active tab
  getCurrentHook: () => {
    errors: ReturnType<typeof useCompilerErrors>["errors"];
    total: number;
    page: number;
    pageSize: number;
    loading: boolean;
    summary: ReturnType<typeof useCompilerErrors>["summary"];
    distribution: ReturnType<typeof useCompilerErrors>["distribution"];
    summaryLoading: boolean;
    distributionLoading: boolean;
  };

  // Actions
  refresh: () => void;
  isRefreshing: boolean;

  // Computed filters
  getFilters: () => ErrorFilters;
}

/**
 * Orchestrator hook that manages all error sources and shared state
 */
export function useErrorConsole(options?: UseErrorConsoleOptions): UseErrorConsoleResult {
  // Tab state
  const [activeTab, setActiveTab] = useState<ErrorSource>(options?.initialTab || 'compiler');

  // Date range state
  const [dateRange, setDateRange] = useState<DateRangeValue>(options?.initialDateRange || '7d');

  // Severity filter state
  const [severityFilter, setSeverityFilter] = useState<ErrorSeverity | 'all'>(
    options?.initialSeverity || 'all'
  );

  // Error code filter
  const [selectedErrorCode, setSelectedErrorCode] = useState<string | null>(null);

  // Refreshing state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Source-specific hooks
  const compilerHook = useCompilerErrors();
  const authHook = useAuthErrors();
  const llmHook = useLlmErrors();
  const functionHook = useFunctionErrors();
  const extractionHook = useExtractionErrors();

  // Compute date filters
  const getFilters = useCallback((): ErrorFilters => {
    const range = DATE_RANGES.find((r) => r.value === dateRange);
    const filters: ErrorFilters = {};

    if (range) {
      filters.startDate = subDays(new Date(), range.days);
      filters.endDate = new Date();
    }

    if (severityFilter !== 'all') {
      filters.severity = severityFilter;
    }

    if (selectedErrorCode) {
      filters.code = selectedErrorCode;
    }

    return filters;
  }, [dateRange, severityFilter, selectedErrorCode]);

  // Get current hook based on active tab
  const getCurrentHook = useCallback(() => {
    switch (activeTab) {
      case 'compiler':
        return compilerHook;
      case 'auth':
        return authHook;
      case 'llm':
        return llmHook;
      case 'function':
        return functionHook;
      case 'extraction':
        return extractionHook;
      default:
        return compilerHook;
    }
  }, [activeTab, compilerHook, authHook, llmHook, functionHook, extractionHook]);

  // Fetch data for current tab
  const fetchCurrentTab = useCallback(async (page: number = 1) => {
    const filters = getFilters();
    const hook = getCurrentHook();

    // Fetch errors
    await hook.fetchErrors(filters, page);
  }, [getFilters, getCurrentHook]);

  // Fetch summary and distribution for current tab
  const fetchCurrentTabMeta = useCallback(async () => {
    const hook = getCurrentHook();
    const filters = getFilters();

    await Promise.all([
      hook.fetchSummary(),
      hook.fetchDistribution(filters.startDate, filters.endDate),
    ]);
  }, [getFilters, getCurrentHook]);

  // Refresh all data for current tab
  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const filters = getFilters();
      const hook = getCurrentHook();

      await Promise.all([
        hook.fetchErrors(filters, 1),
        hook.fetchSummary(),
        hook.fetchDistribution(filters.startDate, filters.endDate),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  }, [getFilters, getCurrentHook]);

  // Effect: Fetch data when tab changes
  useEffect(() => {
    const filters = getFilters();
    const hook = getCurrentHook();

    // Reset selected error code when tab changes
    setSelectedErrorCode(null);

    // Fetch data
    hook.fetchErrors(filters, 1);
    hook.fetchSummary();
    hook.fetchDistribution(filters.startDate, filters.endDate);
  }, [activeTab]); // Only depend on activeTab for tab changes

  // Effect: Refetch when date range changes
  useEffect(() => {
    const filters = getFilters();
    const hook = getCurrentHook();

    hook.fetchErrors(filters, 1);
    hook.fetchDistribution(filters.startDate, filters.endDate);
  }, [dateRange]); // Only depend on dateRange

  // Effect: Refetch when severity filter changes
  useEffect(() => {
    const filters = getFilters();
    const hook = getCurrentHook();

    hook.fetchErrors(filters, 1);
  }, [severityFilter]); // Only depend on severityFilter

  // Effect: Refetch when error code filter changes
  useEffect(() => {
    const filters = getFilters();
    const hook = getCurrentHook();

    hook.fetchErrors(filters, 1);
  }, [selectedErrorCode]); // Only depend on selectedErrorCode

  return {
    // Tab state
    activeTab,
    setActiveTab,

    // Date range state
    dateRange,
    setDateRange,

    // Severity filter state
    severityFilter,
    setSeverityFilter,

    // Error code filter
    selectedErrorCode,
    setSelectedErrorCode,

    // Individual hooks
    compilerHook,
    authHook,
    llmHook,
    functionHook,
    extractionHook,

    // Get current hook
    getCurrentHook,

    // Actions
    refresh,
    isRefreshing,

    // Computed filters
    getFilters,
  };
}
